import { useEffect, useMemo, useState } from 'react';
import { gql } from '@soundxyz/gql-string';
import { LatestNftDocument, useQuery } from '@graphql/reactQuery';
import type { UserNft } from './useMultipleNftsForRelease';
import { useReleaseTokens } from './useReleaseTokens';

/*#__PURE__*/ gql(/* GraphQL */ `
  query latestNft($id: UUID!) {
    nftFromId(id: $id) {
      id
      tokenId
      songSlot
      comment {
        id
        message
      }
      owner {
        id
        publicAddress
      }
      serialNumber
      tierNumber
      isGoldenEgg
    }
  }
`);

export const useSelectReleaseNft = ({
  releaseId,
  preselectedNftId,
}: {
  releaseId: string;
  preselectedNftId?: string;
}) => {
  const {
    nftsOwnedByUser: nfts,
    loading: isLoadingNfts,
    loadMore,
  } = useReleaseTokens({ releaseId });
  const { data: preselectedNft, isLoading: isLoadingPreselectedNft } = useQuery(LatestNftDocument, {
    variables: { id: preselectedNftId ?? '' },
    enabled: !!preselectedNftId,
    staleTime: 0,
  });
  const [initialNftId, setInitialNftId] = useState(preselectedNftId);
  const [selectedNftIndex, setSelectedNftIndex] = useState<number>(0);
  const [nftsDict, setNftsDict] = useState<{ [key: string]: UserNft }>({});
  const isLoading = isLoadingNfts || isLoadingPreselectedNft;

  useEffect(() => {
    const newNftDict: { [key: string]: UserNft } = {};
    const preselectedNftData = preselectedNft?.data.nftFromId;
    if (preselectedNftData) {
      newNftDict[preselectedNftData.id] = preselectedNftData;
      if (initialNftId) {
        setInitialNftId(undefined);
        setSelectedNftIndex(0);
      }
    }
    nfts.forEach(nft => {
      if (!newNftDict[nft.id]) {
        newNftDict[nft.id] = nft;
      }
    });
    setNftsDict(newNftDict);
  }, [initialNftId, nfts, preselectedNft]);

  const nftsOwnedByUser = useMemo(() => {
    return Object.values(nftsDict);
  }, [nftsDict]);

  const selectedNft = useMemo(
    () => (initialNftId ? undefined : nftsOwnedByUser[selectedNftIndex]),
    [initialNftId, nftsOwnedByUser, selectedNftIndex],
  );

  return {
    nftsOwnedByUser,
    loadMore,
    isLoading,
    setSelectedNftIndex,
    selectedNftIndex,
    selectedNft,
  };
};
